import { useState, useEffect, useCallback, useRef } from "react";
import { useWebSocket } from "@/contexts/WebSocketContext";
import type { AiProgressUpdate } from "./AiProgressUpdates";

export interface AiError {
  errorCode: string;
  message: string;
}

interface WebSocketMessage {
  messageType?: string;
  requestId?: string;
  aiMessage?: string;
  toolExecutionStarted?: string;
  toolExecutionEnd?: string;
  toolExecutionFailed?: boolean;
  toolExecutionId?: string;
  toolArguments?: string;
  toolResult?: string;
  chatStreamChunk?: string;
  chatUserMessage?: string;
  chatFinalMessage?: string;
  chatComplete?: boolean;
  chatCancelled?: boolean;
  chatError?: AiError;
  chatConversationId?: number;
}

interface UseAiProgressOptions {
  requestId?: string;
  onChatStreamChunk?: (chunk: string) => void;
  onChatUserMessage?: (message: string) => void;
  onChatComplete?: (conversationId: number | undefined, finalMessage: string | undefined) => void;
  onChatCancelled?: () => void;
  onChatError?: (error: AiError) => void;
}

/**
 * Hook to manage AI progress updates from WebSocket
 * Can be used for both summaries and chat conversations
 */
export const useAiProgress = (options: UseAiProgressOptions = {}) => {
  const { sessionId, lastMessage } = useWebSocket();
  const [progressUpdates, setProgressUpdates] = useState<AiProgressUpdate[]>([]);
  const [activeTools, setActiveTools] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [requestId, setRequestId] = useState<string | undefined>(options.requestId);

  // Use ref to store the current requestId - this is updated immediately by generateRequestId
  const requestIdRef = useRef<string | undefined>(options.requestId);

  // Use ref to store callback options to avoid triggering useEffect on every render
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Clear progress state
  const clearProgress = useCallback(() => {
    setProgressUpdates([]);
    setActiveTools(new Set());
    setIsProcessing(false);
  }, []);

  // Start processing (sets isProcessing to true and clears previous updates)
  const startProcessing = useCallback(() => {
    setProgressUpdates([]);
    setActiveTools(new Set());
    setIsProcessing(true);
  }, []);

  // Generate a new unique request ID for correlating WebSocket messages
  const generateRequestId = useCallback(() => {
    const newRequestId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    requestIdRef.current = newRequestId; // Update ref immediately for filtering
    setRequestId(newRequestId);
    return newRequestId;
  }, []);

  // Listen for WebSocket AI progress updates
  useEffect(() => {
    if (!lastMessage) return;

    const msg = lastMessage as WebSocketMessage;

    if (msg.messageType === "ai_progress_update") {
      // Filter by requestId - only process messages for this specific request
      const currentRequestId = requestIdRef.current;
      if (msg.requestId !== currentRequestId) {
        // Message is for a different request, ignore it
        return;
      }
      // Handle tool execution start
      if (msg.toolExecutionStarted) {
        const toolName = msg.toolExecutionStarted;
        setActiveTools((prev) => new Set([...prev, toolName]));
        setProgressUpdates((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            timestamp: new Date(),
            toolExecutionStarted: toolName,
            toolExecutionId: msg.toolExecutionId,
            toolArguments: msg.toolArguments,
          },
        ]);
      }

      // Handle tool execution end
      if (msg.toolExecutionEnd) {
        const toolName = msg.toolExecutionEnd;
        setActiveTools((prev) => {
          const newSet = new Set(prev);
          newSet.delete(toolName);
          return newSet;
        });
        setProgressUpdates((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            timestamp: new Date(),
            toolExecutionEnd: toolName,
            toolExecutionFailed: msg.toolExecutionFailed,
            toolExecutionId: msg.toolExecutionId,
            toolArguments: msg.toolArguments,
            toolResult: msg.toolResult,
          },
        ]);
      }

      // Handle AI intermediate message
      if (msg.aiMessage) {
        setProgressUpdates((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            timestamp: new Date(),
            aiMessage: msg.aiMessage,
          },
        ]);
      }

      // Handle chat stream chunk - accumulate into progressUpdates
      if (msg.chatStreamChunk) {
        setProgressUpdates((prev) => {
          // Find the last streaming entry and append to it, or create new one
          const lastIdx = prev.length - 1;
          if (lastIdx >= 0 && prev[lastIdx].chatStreamChunk !== undefined) {
            // Append to existing stream chunk
            const updated = [...prev];
            updated[lastIdx] = {
              ...updated[lastIdx],
              chatStreamChunk: (updated[lastIdx].chatStreamChunk || "") + msg.chatStreamChunk,
            };
            return updated;
          } else {
            // Create new stream chunk entry
            return [
              ...prev,
              {
                id: Date.now() + Math.random(),
                timestamp: new Date(),
                chatStreamChunk: msg.chatStreamChunk,
              },
            ];
          }
        });
        if (optionsRef.current.onChatStreamChunk) {
          optionsRef.current.onChatStreamChunk(msg.chatStreamChunk);
        }
      }

      // Handle user message confirmation - add to progressUpdates for display
      if (msg.chatUserMessage) {
        setProgressUpdates((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            timestamp: new Date(),
            chatUserMessage: msg.chatUserMessage,
          },
        ]);
        if (optionsRef.current.onChatUserMessage) {
          optionsRef.current.onChatUserMessage(msg.chatUserMessage);
        }
      }

      // Handle chat completion - add final message if present
      if (msg.chatComplete) {
        setIsProcessing(false);
        setActiveTools(new Set());
        // Add the final AI response as a stream chunk (it's just another incremental message)
        if (msg.chatFinalMessage) {
          setProgressUpdates((prev) => [
            ...prev,
            {
              id: Date.now() + Math.random(),
              timestamp: new Date(),
              chatStreamChunk: msg.chatFinalMessage,
            },
          ]);
        }
        if (optionsRef.current.onChatComplete) {
          optionsRef.current.onChatComplete(msg.chatConversationId, msg.chatFinalMessage);
        }
      }

      // Handle chat cancellation
      if (msg.chatCancelled) {
        setIsProcessing(false);
        setActiveTools(new Set());
        if (optionsRef.current.onChatCancelled) {
          optionsRef.current.onChatCancelled();
        }
      }

      // Handle chat error - add to progress updates so it appears in correct position
      if (msg.chatError) {
        setIsProcessing(false);
        setActiveTools(new Set());
        setProgressUpdates((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            timestamp: new Date(),
            chatError: msg.chatError,
          },
        ]);
        if (optionsRef.current.onChatError) {
          optionsRef.current.onChatError(msg.chatError);
        }
      }
    }
  }, [lastMessage]); // Only depend on lastMessage, not options

  return {
    sessionId,
    requestId,
    generateRequestId,
    progressUpdates,
    activeTools,
    isProcessing,
    clearProgress,
    startProcessing,
  };
};

export default useAiProgress;
